import { FileUploadOutlined } from "@mui/icons-material";
import { IconButton, TextField, Box, CircularProgress, Typography } from "@mui/material";
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import { useAlert } from "react-alert";
import {
  ChangeEvent,
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";
import {
  useUploadToAzureMutation,
} from "../../redux/Documents/documentSlice";
import { useGenerateUploadLinkMutation } from "../../redux/Assessment/assessmentApis";

export type FileInputRef = {
  files: string[];
  clearInput: () => void;
};

const UploadFiles = (
  { label, isMultiSelect = false, name },
  ref: Ref<FileInputRef>,
) => {
  const [selectedFileNames, setSelectedFileNames] = useState<string[]>([]);
  const [files, setFiles] = useState<string[]>([]);
  const [getLink, { isLoading }] =
    useGenerateUploadLinkMutation();
  const [
    uploadFile,
    { isLoading: uploadIsLoading },
  ] = useUploadToAzureMutation();
  const alert = useAlert();

  const clearInput = useCallback(() => {
    setFiles([]);
    setSelectedFileNames([]);
  }, []);

  useImperativeHandle(ref, () => ({
    files,
    clearInput,
  }));

  const uploadAndGetUrl = async (file: any) => {
    try {
      const dataLink = await getLink({
        fileName: file.name,
        id: "",
      });
      if (dataLink.error) {
        alert.error("Something went wrong !!");
        return;
      }

      const link_ = dataLink.data?.link ?? "";
      const responseAzure = await uploadFile({
        link: link_ ?? "",
        file: file,
        fileName: "assessment-submission",
        id: "",
      });
      if (responseAzure.error) {
        alert.error("Something went wrong !!");
        return
      }
      const url_ = new URL(link_);
      const filename = url_.pathname.split("/").pop() ?? "";
      setFiles([...files, filename]);
    } catch (error) {
      alert.error("Something went wrong !!");
    }
  };

  const generateLink = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.length) {
        const selectedFiles = e.target.files;
        let totalFileSize = 0;
        for (let x = 0; x < selectedFiles.length; x++) {
          if (selectedFiles[x].size > 5e6) {
            alert.info(`File ${selectedFiles[x].name} is too large, please upload a file smaller than 5 MB`);
            return;
          }
          totalFileSize += selectedFiles[x].size;
        }
        //validate file size
        if (totalFileSize > 5e6) {
          alert.error("Total file size exceeds 5 MB !");
          return;
        }

        let filesLabel: string[] = [];
        let uploadPromise: Promise<any>[] = [];
        for (let i = 0; i < selectedFiles.length; i++) {
          const thisFile = selectedFiles[i];
          //Prapare label
          filesLabel.push(thisFile.name);
          //generate link
          const sanitizedFileName = thisFile.name
            .replace(/ /g, "_") // replace spaces with underscores
            .replace(/[^a-zA-Z0-9._-]/g, ""); // remove special characters except . _ -
          const modifiedFile = new File([thisFile], sanitizedFileName);
          uploadPromise.push(uploadAndGetUrl(modifiedFile));
        }
        setSelectedFileNames(filesLabel);
        await Promise.all(uploadPromise);
      }
    },
    [getLink],
  );

  return (
    <>
      <TextField
        variant="outlined"
        type="text"
        label={selectedFileNames.length ? selectedFileNames.join(", ") : label}
        fullWidth
        value={""}
        disabled
        InputProps={{
          endAdornment:
            uploadIsLoading || isLoading ? (
              <Box width={24} height={24}>
                <CircularProgress size={24} color="primary" />
              </Box>
            ) : (
              <IconButton component="label">
                <FileUploadOutlined />
                <input
                  accept=".docx,.doc,.txt,application/msword,text/plain, application/pdf, .zip"
                  style={{ display: "none" }}
                  type="file"
                  name={name}
                  hidden
                  onChange={generateLink}
                  multiple={isMultiSelect}
                />
              </IconButton>
            ),
        }}
      />
      
      <Typography
        variant="body2"
        component="div"
        display={"flex"}
        marginTop={"8px"}
      >
        <InfoOutlineIcon fontSize="small"/>
        <Typography component="span" fontStyle={"italic"}
        fontSize={"small"} marginLeft={"3px"}>Upload files in .docx, .doc, .txt, .pdf, .zip formats only (Max size: 5 MB)</Typography>
      </Typography>
    </>
  );
};

export default forwardRef(UploadFiles);
